import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Box, IconButton, Typography, SxProps, Theme } from "@mui/material";
import { ReactNode } from "react";
import { useNavigate } from "react-router";

import { CustomBreadcrumbs } from "../../components/common/CustomBreadcrumbs";
import { useCelleraTheme } from "../../theme/useCelleraTheme";

export interface BreadcrumbItem {
  /**
   * Display label for the breadcrumb
   */
  label: string;
  /**
   * Navigation href for the breadcrumb
   */
  href: string;
  /**
   * Order of the breadcrumb (for sorting)
   */
  order: number;
}

export interface ActionButton {
  /**
   * Icon component for the button
   */
  icon: ReactNode;
  /**
   * Click handler for the button
   */
  onClick: () => void;
  /**
   * Tooltip text for the button
   */
  tooltip?: string;
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Custom styling for the button
   */
  sx?: SxProps<Theme>;
}

export interface CelleraPageContainerProps {
  /**
   * Breadcrumb items for navigation
   */
  breadcrumbs?: BreadcrumbItem[];
  /**
   * Last breadcrumb item (displayed without href)
   */
  lastBreadcrumbItem?: string;
  /**
   * Page title
   */
  title: string;
  /**
   * Icon component for the page title
   */
  icon?: ReactNode;
  /**
   * Action buttons to display in the header
   */
  actionButtons?: ActionButton[];
  /**
   * Main content of the page
   */
  children: ReactNode;
  /**
   * Custom styling for the main container
   */
  containerSx?: SxProps<Theme>;
  /**
   * Custom styling for the content area
   */
  contentSx?: SxProps<Theme>;
  /**
   * Whether to show default back button
   */
  showBackButton?: boolean;
  /**
   * Custom back button handler (defaults to navigating to /patients)
   */
  onBack?: () => void;
  /**
   * Whether to show default refresh button
   */
  showRefreshButton?: boolean;
  /**
   * Custom refresh button handler
   */
  onRefresh?: () => void;
  /**
   * Custom styling for the title area
   */
  titleSx?: SxProps<Theme>;
  rightCornerIconAction?: ActionButton;
}

function PatientDetailPageContainer({
  breadcrumbs,
  lastBreadcrumbItem,
  title,
  icon,
  actionButtons = [],
  children,
  containerSx,
  contentSx,
  showBackButton = true,
  onBack,
  showRefreshButton = true,
  onRefresh,
  titleSx,
  rightCornerIconAction,
}: CelleraPageContainerProps) {
  const { colors } = useCelleraTheme();
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/patients");
    }
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  // Build action buttons array
  const allActionButtons: ActionButton[] = [];

  // Add default back button if enabled
  if (showBackButton) {
    allActionButtons.push({
      icon: <ArrowBackIcon />,
      onClick: handleBack,
      tooltip: "Go back",
    });
  }

  // Add default refresh button if enabled
  if (showRefreshButton) {
    allActionButtons.push({
      icon: <RefreshIcon />,
      onClick: handleRefresh,
      tooltip: "Refresh",
    });
  }

  // Add custom action buttons
  allActionButtons.push(...actionButtons);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        backgroundColor: colors.background.paper,
        ...containerSx,
      }}
    >
      {/* Header with breadcrumbs and action buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        {breadcrumbs && (
          <CustomBreadcrumbs
            items={breadcrumbs}
            lastItem={lastBreadcrumbItem ?? ""}
          />
        )}

        {allActionButtons.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {allActionButtons.map((button, index) => (
              <IconButton
                key={index}
                onClick={button.onClick}
                disabled={button.disabled}
                sx={{
                  color: "text.secondary",
                  ...button.sx,
                }}
                size="small"
                title={button.tooltip}
              >
                {button.icon}
              </IconButton>
            ))}
          </Box>
        )}
      </Box>

      {/* Content area */}
      <Box
        sx={{
          border: `2px solid ${colors.border.main}`,
          borderRadius: 1,
          height: "100%",
          p: 2,
          ...contentSx,
        }}
      >
        {/* Page title with icon */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              m: 2,
              ...titleSx,
            }}
          >
            {icon && (
              <Box sx={{ color: colors.primary.main, fontSize: 30 }}>
                {icon}
              </Box>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
              }}
            >
              {title}
            </Typography>
          </Box>
          {rightCornerIconAction && (
            <Box sx={{ color: colors.primary.main, fontSize: 30, pr: 3 }}>
              {rightCornerIconAction.icon}
            </Box>
          )}
        </Box>

        {children}
      </Box>
    </Box>
  );
}

export default PatientDetailPageContainer;
